import type { FormlyFieldConfig } from '@ngx-formly/core';
import { noticeSourceKey, type NoticeRegistry } from './notice-chip.types';

/**
 * The registry key a Formly FIELD publishes its notice under (`DetailsService.setNotice`):
 * `<formly type>:<keyPath>`, with the keyPath read off `props.keyPath` (set by the form
 * generator) and falling back to the plain `key`. ONE derivation for every publisher — the
 * required-documents checklist and the linked-items list used to each carry a private copy, and
 * `tabs.component.ts` matches the keyPath half against a tab's own fields (`#collectKeyPaths`,
 * same `props.keyPath ?? key` rule): a format change in one copy only would silently drop that
 * field's count from its tab badge while the other publisher kept working.
 *
 * Deriving it from the field on every call is also what makes a SECOND mount of the same field
 * (the Overzicht own-view card) collapse onto the same registry entry instead of fighting the
 * first.
 */
export function noticeSourceKeyForField(
  fieldType: string,
  field: Pick<FormlyFieldConfig, 'key' | 'props'> | undefined
): string {
  const keyPath =
    (field?.props?.['keyPath'] as string | undefined) ?? (typeof field?.key === 'string' ? field.key : '');
  return noticeSourceKey(fieldType, keyPath);
}

/**
 * The page's notice registry, when this form is mounted on an entry detail page — `undefined` on
 * hosts that build their own `options.formState` without a `DetailsService` (the /profiel and
 * /bedrijfsinstellingen pages, Storybook, the builder preview), so a publisher can simply skip.
 * Same rule as `modulesStoreOf` (display-context.util.ts): `formState` is a shared bag several
 * features write into, so the guard checks the one member the caller will actually call rather
 * than trusting the key's presence.
 */
export function noticeRegistryOf(formState: unknown): NoticeRegistry | undefined {
  const candidate = (formState as { detailsService?: unknown } | undefined)?.detailsService as
    | Partial<NoticeRegistry>
    | null
    | undefined;
  return typeof candidate?.setNotice === 'function' ? (candidate as NoticeRegistry) : undefined;
}
